import type { ExistingFieldMapping } from '../views/shared/mapping-field-row.js';

/**
 * Was die Vorbelegung von einem Anzeige-Feld braucht. Bewusst ein eigener, schmaler Typ:
 * `DisplayField` traegt zwei Dutzend Felder, von denen hier drei zaehlen.
 */
export interface VorbelegungsFeld {
  key: string;
  originalFieldGroup: string;
  /** Vorschlag aus dem Schema (z.B. `article.manufacturer` fuer die Marke). */
  defaultSourceKey?: string | undefined;
  /** Kauflands Werteliste. Nur die Laenge zaehlt hier. */
  allowedValues?: ReadonlyArray<unknown> | undefined;
}

/** Das Kaufland-Feld „Maße" erwartet Freitext „B x H x T" plus Einheit. `resolveSource` baut
 *  ihn aus `netMetric.sizeX/Y/Z` + `sizeUnit.abbreviation`; im Dropdown heisst die Quelle
 *  „Maße komplett (B×H×T, netto)". */
const MASSE_QUELLE = 'article.netMetric.dimensions';

/**
 * Der Anfangszustand des Mapping-Editors: gespeicherte Zeilen, sonst Vorschlaege.
 *
 * ANLASS (2026-08-20): Der Betreiber stellte „Material" auf „nicht gemappt" und speicherte
 * — danach stand dort wieder „Fester Wert". An der Live-DB gemessen: Das Speichern war
 * korrekt, die Zeile war unmittelbar danach weg. Der Editor legte sie beim naechsten LADEN
 * neu an, weil `material` (attribute_id 1051) ein Shared-Set-Attribut mit 77 Werten ist und
 * Felder mit Werteliste als leerer Festwert vorbelegt wurden.
 *
 * Der Denkfehler steckte in der Gleichsetzung „keine Zeile = noch nie eingerichtet". Sobald
 * ein Profil gespeichert IST, heisst eine fehlende Zeile das Gegenteil: ausdruecklich nicht
 * gemappt. `profilVorhanden` trennt die beiden Faelle.
 *
 * Vorbelegungen sind ein Einrichtungshilfsmittel, keine Regel. Sie gelten deshalb nur beim
 * ersten Mal — und dann fuer ALLE Arten gleich (Maße, `defaultSourceKey`, Werteliste);
 * Vorgabe des Betreibers: „Es ist egal, welches Attribut."
 *
 * Dass die Maske danach mehr Felder als „nicht gemappt" zeigt, ist kein Verlust: Der Push
 * liest das gespeicherte Profil, nie die Vorbelegung. Ein nur vorbelegtes, nie gespeichertes
 * Feld war ohnehin ohne Wirkung — die Maske hoert also auf, etwas zu behaupten, was nie galt.
 *
 * Pure -- keine IO.
 */
export function initialeMappings(
  displayFields: readonly VorbelegungsFeld[],
  profilFelder: readonly ExistingFieldMapping[],
  profilVorhanden: boolean,
): Map<string, ExistingFieldMapping> {
  const mappings = new Map<string, ExistingFieldMapping>();

  for (const f of displayFields) {
    const gespeichert = profilFelder.find((m) => m.kaufland_field_key === f.key);
    if (gespeichert) {
      mappings.set(f.key, gespeichert);
      continue;
    }
    // Ab hier: keine gespeicherte Zeile. Bei vorhandenem Profil ist das eine Aussage --
    // nicht gemappt -- und keine Luecke, die zu fuellen waere.
    if (profilVorhanden) continue;

    const vorbelegung = vorschlagFuer(f);
    if (vorbelegung) mappings.set(f.key, vorbelegung);
  }

  return mappings;
}

/** Der Einrichtungs-Vorschlag fuer ein Feld, oder `null`, wenn es keinen gibt. */
function vorschlagFuer(f: VorbelegungsFeld): ExistingFieldMapping | null {
  const basis = {
    kaufland_field_key: f.key,
    field_group: f.originalFieldGroup,
    vario_path: null,
    fixed_value: null,
    template: null,
    fallback: null,
  } as const;

  if (f.key === 'product_dimensions') {
    return { ...basis, source_type: 'vario_path', vario_path: MASSE_QUELLE };
  }
  if (f.defaultSourceKey) {
    return { ...basis, source_type: 'vario_path', vario_path: f.defaultSourceKey };
  }
  // WERTELISTEN-FELDER BEKOMMEN KEINE VORBELEGUNG MEHR (2026-08-24).
  //
  // Bis hierher wurden sie als LEERER Festwert vorbelegt, damit sofort das Dropdown
  // erscheint. Das war ein teurer Komfort: Wer die Maske eines noch nicht gespeicherten
  // Profils einfach speicherte, schrieb damit fuer jedes Wertelisten-Feld eine Zeile
  // `source_type='fixed_value'` mit leerem Inhalt -- und ein leerer Festwert ist seit
  // Migration 0054 ein LOESCHAUFTRAG an Kaufland
  // (packages/kaufland-product-data/src/index.ts:969 sendet ein leeres Array, Kaufland
  // entfernt das Attribut).
  //
  // Gemessen am 2026-08-24: Im de-Profil der Kategorie 22481 standen neun solcher Zeilen,
  // darunter `title` und `short_description`. Migration 0055 hatte dieselbe Fehlerklasse
  // schon einmal aufgeraeumt (22 Zeilen) -- sie kam zurueck, weil die Ursache hier sass.
  //
  // Vorgabe des Auftraggebers: "Du setzt halt fester Wert wo du Werte eintraegst."
  // Ein Festwert entsteht also nur MIT Inhalt. Ohne Vorbelegung startet das Feld auf
  // "nicht gemappt"; der Haendler waehlt "Fester Wert" und bekommt dann das Dropdown.
  return null;
}
